import {
  Box,
  Flex,
  Text as ChakraText,
  Tooltip,
  IconButton,
  Textarea,
  HStack,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { VscSparkle } from 'react-icons/vsc'
import { useFetch, CachePolicies } from 'use-http'
import useToast from '../../../hooks/useToast'
import type {
  FileDetailsErrorResponse,
  FileDetailsResponse,
} from '../../../interfaces/FileDetailsResponse'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import { MediaLibraryActions, MLContext } from '../../../providers/MLProvider'
import { PermissionsEnum } from '../../../../utils'
import PermissionGuard from '../../PermissionGuard'

const FileDescription = ({
  fileDetails,
}: {
  fileDetails: FileDetailsResponse
}): React.ReactElement => {
  const isDescriptionEditable = fileDetails.type === 'file'
  const isImage = fileDetails.fileType === 'image'

  const { request, loading, response, error } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [, dispatch] = useContext(MLContext)

  const { t } = useTranslation()
  const toast = useToast()

  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState(fileDetails.description ?? '')
  const [tempDescription, setTempDescription] = useState(description)
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    setDescription(fileDetails.description ?? '')
    setTempDescription(fileDetails.description ?? '')
  }, [fileDetails.fileId, fileDetails.description])

  const handleApprove = async (): Promise<void> => {
    const data = (await request.patch(
      `/api/v2/files/${fileDetails.fileId}/details`,
      {
        description: tempDescription.trim(),
      },
    )) as FileDetailsResponse | FileDetailsErrorResponse

    if (response.ok) {
      setDescription((data as FileDetailsResponse).description ?? '')
      setTempDescription((data as FileDetailsResponse).description ?? '')
      dispatch({
        type: MediaLibraryActions.UPDATE_FILES,
        payload: [
          {
            ...fileDetails,
            description: (data as FileDetailsResponse).description || '',
          },
        ],
      })
      toast({
        description: t(
          'update_description_success',
          'Description updated successfully.',
        ),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
      setIsEditing(false)
    } else {
      toast({
        title: t('update_description_error', 'Description update failed.'),
        description:
          (data as FileDetailsErrorResponse).message ??
          (data as FileDetailsResponse).description ??
          error?.message ??
          t('unknown_error', 'An unknown error occurred'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  const handleCancel = (): void => {
    setTempDescription(description)
    setIsEditing(false)
  }

  const handleAutoDescription = async (): Promise<void> => {
    setIsGenerating(true)

    const data = await request.patch(
      `/api/v2/files/${fileDetails.fileId}/details`,
      {
        extensions: [{ name: 'ai-auto-description' }],
      },
    )

    if (response.ok) {
      switch (data.extensionStatus?.['ai-auto-description']) {
        case 'pending':
          setIsGenerating(false)
          setIsEditing(false)
          toast({
            title: t('desc_will_update', 'Description will update shortly.'),
            description: t(
              'ai_generating_desc',
              'AI is generating your description.',
            ),
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
          break
        case 'failed':
          setIsGenerating(false)
          setIsEditing(false)
          toast({
            description: t(
              'desc_generate_failed',
              'Failed to generate a description.',
            ),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
          break
        default:
          setDescription(data.description || '')
          setTempDescription(data.description || '')
          setIsEditing(false)
          setIsGenerating(false)
          dispatch({
            type: MediaLibraryActions.UPDATE_FILES,
            payload: [
              {
                ...fileDetails,
                description: data.description || '',
              },
            ],
          })
          toast({
            description: t(
              'desc_generate_success',
              'Successfully applied auto description.',
            ),
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
      }
    } else {
      setIsGenerating(false)
      setIsEditing(false)
      toast({
        title: t('update_description_error', 'Description update failed.'),
        description:
          data?.message ||
          data?.description ||
          error?.message ||
          t('unknown_error', 'An unknown error occurred'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  return (
    <Box>
      {!isEditing ? (
        <Flex align="flex-start" gap="8px" minH="21px">
          <ChakraText
            whiteSpace="pre-line"
            wordBreak="break-word"
            flex="1"
            className="description"
            fontWeight="500"
            color={description ? 'appDarkIndigo' : 'imagekitGray.500'}
          >
            {description || t('no_description', 'No description')}
          </ChakraText>
          {!isPublicLink && (
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <Tooltip label={t('Edit', 'Edit')} hasArrow>
                    <IconButton
                      icon={<EditIcon />}
                      h="21px"
                      size="sm"
                      variant="ghost"
                      color={
                        isDescriptionEditable ? 'appBlue' : 'imagekitGray.500'
                      }
                      aria-label={t('Edit Description', 'Edit Description')}
                      onClick={() => void setIsEditing(true)}
                      isDisabled={!isDescriptionEditable}
                    />
                  </Tooltip>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          )}
        </Flex>
      ) : (
        <Box>
          <Box position="relative">
            <Textarea
              value={tempDescription}
              onChange={e => void setTempDescription(e.target.value)}
              pr="2rem"
              resize="none"
            />
            <Tooltip
              label={t(
                'generate_description_for_image',
                'Generate description for the image',
              )}
              hasArrow
            >
              <IconButton
                icon={<VscSparkle />}
                isLoading={isGenerating}
                isDisabled={!isImage || loading}
                spinnerPlacement="start"
                size="sm"
                variant="ghost"
                position="absolute"
                top="0.3rem"
                right="0.3rem"
                zIndex="1"
                aria-label={t(
                  'generate_description_for_image',
                  'Generate description for the image',
                )}
                onClick={handleAutoDescription}
              />
            </Tooltip>
          </Box>
          <HStack mt={2} justify="flex-end" spacing={3}>
            <IconButton
              icon={<CheckIcon />}
              size="sm"
              borderRadius="md"
              aria-label={t('save', 'Save')}
              onClick={handleApprove}
              isDisabled={isGenerating || loading}
            />
            <IconButton
              icon={<CloseIcon />}
              variant="outline"
              size="sm"
              borderRadius="md"
              aria-label={t('cancel', 'Cancel')}
              onClick={handleCancel}
              isDisabled={isGenerating || loading}
            />
          </HStack>
        </Box>
      )}
    </Box>
  )
}

export default FileDescription
